import {Component} from 'panel';
import {produce} from 'immer';

import {BannerAction} from 'common/components/banner/types';
import {reporter} from 'common/report/error-reporting';
import {Persistence} from 'common/report/persistence';
import {getProjectId, getReportName, getWorkspaceId} from 'common/report/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {unique} from 'common/util/array';

import {getBanners, GetBannersResponse} from './api';
import {BannerEventNames, BannerEventProps, REDIRECT_BANNERS, RedirectBannerUrlParams} from './constants';
import {ReportBanner, ReportBannersState} from './types';
import {template} from './template';
import css from './index.styl?inline';

export class ReportBanners extends Component<ReportBannersState> {
  private persistence: Persistence;

  static get attrsSchema() {
    return {
      report: `string`,
    };
  }

  get config() {
    return {
      template,
      css,
      useShadowDom: true,
      defaultState: {
        banners: [],
      },
      helpers: {
        handleBannerAction: (ev, bannerIdx: number) => {
          const {actionName} = ev.detail;

          if (actionName === BannerAction.Dismiss) {
            this.hideBanner(bannerIdx, true);
          } else if (actionName !== BannerAction.None) {
            this.hideBanner(bannerIdx);
          }
        },
        getReportName: () => {
          return this.attr(`report`) || getReportName();
        },
        areBannersVisible: (): boolean => {
          return this.state.banners.filter((banner) => banner.visible).length > 0;
        },
      },
    };
  }

  connectedCallback() {
    super.connectedCallback();

    // we need to sync after DOMContentLoaded since projectId and workspaceId can come from mp.report.globals
    // which are initialized after mp-report-banner
    // prettier-ignore
    window.addEventListener(`DOMContentLoaded`, () => {this.syncBanners()}, {once: true});
  }

  async syncBanners() {
    const projectId = getProjectId();
    this.persistence = new Persistence(`banners:${projectId}`);

    // update static banners first before requesting from API
    this.update({banners: this.getRedirectBanners()});
    // don't load any banners if you don't have a project id as it they won't load properly
    if (projectId) {
      try {
        const bannersResponse = await getBanners({
          projectId,
          workspaceId: getWorkspaceId(),
          reportName: this.helpers.getReportName(),
        });

        this.update({
          banners: unique([...this.state.banners, ...this.getBannersFromResponse(bannersResponse)], {
            // ensure duplicate banners (with same title+message) are not added
            // duplicates can occur when switching quickly between reports with no page reload
            hash: (banner) => `${banner.title}|${banner.message}`,
          }),
        });
      } catch (err) {
        reporter.error(err);
      }
    }
  }

  /**
   * Get banners to be shown based on query params.
   * Typical use case is showing a confirmation message after a redirect flow.
   */
  getRedirectBanners(): ReportBanner[] {
    const url = new URL(window.location.toString());
    const banners = Object.values(RedirectBannerUrlParams)
      .map((redirectBannerName) => {
        if (url.searchParams.has(redirectBannerName)) {
          url.searchParams.delete(redirectBannerName);
          return REDIRECT_BANNERS[redirectBannerName];
        }
        return null;
      })
      .filter(Boolean);

    window.history.replaceState(null, null, url.href);
    return banners;
  }

  bannersResponseToReportBanners(bannersResponse: GetBannersResponse): ReportBanner[] {
    return bannersResponse.map(
      (bannerData) =>
        ({
          type: bannerData.type,
          ctas: bannerData.ctas,
          message: bannerData.message,
          name: bannerData.name || bannerData.title,
          persistDismissKey: bannerData.persistDismissKey,
          size: bannerData.size,
          title: bannerData.title,
          onShow: bannerData.onShow,
          visible: bannerData.persistDismissKey && this.persistence.get(bannerData.persistDismissKey) ? false : true,
        } as ReportBanner),
    );
  }

  getBannersFromResponse(bannersResponse: GetBannersResponse) {
    const banners = this.bannersResponseToReportBanners(bannersResponse).filter(
      (banner) => banner.visible && banner.type && banner.message,
    );

    if (banners.length) {
      mixpanel.track(BannerEventNames.BannersShown, {
        [BannerEventProps.NumBannersShown]: banners.length,
      });
      banners.forEach((banner) => {
        this.trackBannerEvent(BannerEventNames.BannerShown, banner);
        if (banner.onShow) {
          const {trackEventName, trackProps} = banner.onShow;
          mixpanel.track(trackEventName, trackProps);
        }
      });
    }

    return banners;
  }

  trackBannerEvent(eventName: string, banner: ReportBanner) {
    mixpanel.track(eventName, {
      [BannerEventProps.BannerType]: banner.type,
      [BannerEventProps.BannerMessage]: banner.message,
      [BannerEventProps.BannerName]: banner.name,
      [BannerEventProps.BannerTitle]: banner.title,
      [BannerEventProps.ReportName]: this.helpers.getReportName(),
      [BannerEventProps.ProjectId]: getProjectId(),
      [BannerEventProps.WorkspaceId]: getWorkspaceId(),
    });
  }

  hideBanner(bannerIdx: number, dismissed = false) {
    const {banners} = this.state;
    const banner = banners[bannerIdx];
    if (dismissed && banner.persistDismissKey) {
      this.persistence.set(banner.persistDismissKey, Date.now());
    }
    this.update({
      banners: produce(banners, (banners) => {
        banners[bannerIdx].visible = false;
      }),
    });
    this.trackBannerEvent(BannerEventNames.BannerHidden, banner);
  }
}

export const ReportBannersEl = defineAndMakeJsxFactory(`mp-report-banners`, ReportBanners);
